/**
 * The stops the poster design shows, each with its days and photo.
 *
 * Built from allRide.attractionsByDay rather than allRide.highLevelRideLocations:
 * the backend rebuilds that list with getHighLevelRideLocations after the day
 * loop, so its numberOfDays does not line up with the days and a stop's photo
 * could not be found from it.
 *
 * A stay is a run of consecutive days with the same accommodation, booked first
 * and base-trip second (the same choice SingleRide's activAccommodation makes).
 * A day without one belongs to the stay before it; a day without one and no
 * stay before it is a stop of its own, named after its first attraction.
 *
 * How many points the poster shows:
 * - more than PER_STAY_ABOVE stays: one point per stay.
 * - otherwise a poster of a few stops looks empty, so every day is a point,
 *   but no more than MAX_POSTER_STOPS. Over that, the shortest stay (of more
 *   than one day) goes back to being a single point, then the next shortest,
 *   until the poster fits.
 * A stay's first day keeps the stay's name; its later days are named after
 * their first attraction, so the same city does not repeat down the poster.
 *
 * The ride manager can replace both (presentation_settings.poster, edited in
 * PosterEditor): their own splits of the days into stops, and a photo per
 * stop. Without them, all of the above is automatic.
 *
 * The photo is the hotel's, then the stop's attractions' in order, the same
 * order the brochure's collect_stay_photo_urls uses; the hotel only for the
 * stay's first part. A photo another stop already shows is used only when the
 * stop has nothing else. Null when there is none at all.
 */

const PER_STAY_ABOVE = 4;
const MAX_POSTER_STOPS = 15;

// The shape of the poster's settings this code reads and saves, stored in the
// section as "v". Must match the poster's version in PRESENTATION_SETTINGS in
// the backend's travelerblog/ride/views.py, which upgrades older sections
// before sending them. A newer one is not read (designSettings), so a page
// cached from before a change draws the poster automatic, not misread.
const POSTER_SETTINGS_VERSION = 1;

// The longest stop name the manager can type. Must match POSTER_MAX_NAME_LENGTH
// in the backend's travelerblog/ride/views.py, which refuses a longer one.
const POSTER_NAME_MAX_LENGTH = 40;

const hasId = item => !!(item && item.id);

function dayAccommodation(day) {
  if (hasId(day.accommodation)) return day.accommodation;
  if (hasId(day.base_trip_accommodation)) return day.base_trip_accommodation;
  return null;
}

function groupDaysIntoStays(attractionsByDay) {
  const stays = [];
  (attractionsByDay || []).forEach((day, index) => {
    const accommodation = dayAccommodation(day || {});
    const entry = { index, attractions: ((day && day.daily_attractions) || []).filter(Boolean) };
    const last = stays[stays.length - 1];
    const continuesLast = last && (accommodation
      ? last.accommodationId === accommodation.id
      : last.accommodationId !== null);

    if (continuesLast) {
      last.days.push(entry);
      return;
    }
    const firstAttraction = entry.attractions.find(attraction => attraction.title);
    stays.push({
      accommodationId: accommodation ? accommodation.id : null,
      accommodation,
      name: accommodation
        ? (accommodation.cuntry_and_city || accommodation.name || '')
        : (firstAttraction ? firstAttraction.title : ''),
      isContinuation: false,
      days: [entry],
    });
  });
  return stays;
}

// One point per day, except the stays that must stay whole for the poster to
// fit in maxStops: the shortest ones, since only those are taken back first.
function splitIntoDays(stays, maxStops) {
  const whole = new Set();
  let count = stays.reduce((sum, stay) => sum + stay.days.length, 0);
  const byLength = stays.map((stay, i) => i)
    .filter(i => stays[i].days.length > 1)
    .sort((a, b) => stays[a].days.length - stays[b].days.length || a - b);
  for (const i of byLength) {
    if (count <= maxStops) break;
    whole.add(i);
    count -= stays[i].days.length - 1;
  }
  return [].concat(...stays.map((stay, i) => whole.has(i)
    ? [stay]
    : stay.days.map((entry, d) => Object.assign({}, stay, { days: [entry], isContinuation: d > 0 }))));
}

function chooseStops(stays, perStayAbove, maxStops) {
  return stays.length > perStayAbove ? stays : splitIntoDays(stays, maxStops);
}

function choosePhoto(stop, used) {
  const attractions = [].concat(...stop.days.map(day => day.attractions));
  const hotelImage = stop.accommodation && stop.accommodation.image;
  const candidates = (stop.isContinuation ? [] : [hotelImage])
    .concat(attractions.map(attraction => attraction.image))
    .concat(stop.isContinuation ? [hotelImage] : [])
    .filter(Boolean);
  return candidates.find(image => !used.has(image)) || candidates[0] || null;
}

// The manager's own stops: one per range of days, starting at each split.
// A stop that starts where a stay starts takes the stay's name and hotel
// photo; one that starts inside a stay is a continuation of it.
function stopsFromSplits(stays, splits) {
  const days = [].concat(...stays.map(stay => stay.days.map((entry, d) => ({ entry, stay, startsStay: d === 0 }))));
  return splits.map((firstDay, i) => {
    const lastDay = i + 1 < splits.length ? splits[i + 1] - 1 : days.length;
    const first = days[firstDay - 1];
    return Object.assign({}, first.stay, {
      days: days.slice(firstDay - 1, lastDay).map(day => day.entry),
      isContinuation: !first.startsStay,
    });
  });
}

/**
 * The manager's splits, made safe for a trip of dayCount days: whole days in
 * 1..dayCount, ascending, no repeats, starting at day 1. Null when there are
 * none to use (automatic). Days past the end are dropped, so a trip shortened
 * after the manager edited its poster still shows one.
 */
function normalizeSplits(splits, dayCount) {
  if (!Array.isArray(splits) || !dayCount) return null;
  const days = Array.from(new Set(splits.filter(day =>
    Number.isInteger(day) && day >= 1 && day <= dayCount))).sort((a, b) => a - b);
  if (!days.length) return null;
  if (days[0] !== 1) days.unshift(1);
  return days;
}

/**
 * settings: the ride's presentation_settings.poster, or nothing for automatic.
 *   splits: the first day of each stop, replacing the automatic grouping.
 *   photos: a stop's first day -> the photo the manager picked for it.
 *   names: a stop's first day -> the name the manager typed for it, shown
 *     instead of the automatic one; on the poster only, the trip's places
 *     keep their own names.
 */
function buildPosterStops(attractionsByDay,
  { perStayAbove = PER_STAY_ABOVE, maxStops = MAX_POSTER_STOPS, settings = null } = {}) {
  const stays = groupDaysIntoStays(attractionsByDay);
  const dayCount = stays.reduce((sum, stay) => sum + stay.days.length, 0);
  const splits = normalizeSplits(settings && settings.splits, dayCount);
  const picked = (settings && settings.photos) || {};
  const names = (settings && settings.names) || {};
  const stops = splits ? stopsFromSplits(stays, splits) : chooseStops(stays, perStayAbove, maxStops);

  // The manager's picks first, so the automatic choice for the other stops
  // steers clear of them.
  const used = new Set(stops.map(stop => picked[String(stop.days[0].index + 1)]).filter(Boolean));
  return stops.map(stop => {
    const firstDay = stop.days[0].index + 1;
    const firstAttraction = [].concat(...stop.days.map(day => day.attractions))
      .find(attraction => attraction.title && attraction.title.trim());
    const typed = typeof names[String(firstDay)] === 'string' ? names[String(firstDay)].trim() : '';
    const name = typed || (stop.isContinuation && firstAttraction ? firstAttraction.title : stop.name);
    let photo = picked[String(firstDay)] || null;
    if (!photo) {
      photo = choosePhoto(stop, used);
      if (photo) used.add(photo);
    }
    return {
      accommodationId: stop.accommodationId,
      name: (name || '').trim(),
      firstDay,
      lastDay: stop.days[stop.days.length - 1].index + 1,
      dayIndex: firstDay - 1,
      photo,
    };
  });
}

/**
 * The photos a manager can pick for the stop covering days firstDay..lastDay:
 * each day's hotel photo, then its places' photos, in day order, no repeats.
 * [{ url, label }], label being the hotel's or the place's name.
 */
function posterPhotoOptions(attractionsByDay, firstDay, lastDay) {
  const options = [];
  const seen = new Set();
  const add = (url, label) => {
    if (!url || seen.has(url)) return;
    seen.add(url);
    options.push({ url, label: (label || '').trim() });
  };
  (attractionsByDay || []).slice(firstDay - 1, lastDay).forEach(day => {
    const accommodation = day && dayAccommodation(day);
    if (accommodation) add(accommodation.image, accommodation.name || accommodation.cuntry_and_city);
    ((day && day.daily_attractions) || []).filter(Boolean)
      .forEach(attraction => add(attraction.image, attraction.title));
  });
  return options;
}

/**
 * A short name for every day, for the editor's day list: its accommodation's
 * city (or name), else its first named place, else ''.
 */
function posterDayNames(attractionsByDay) {
  return (attractionsByDay || []).map(day => {
    const accommodation = day && dayAccommodation(day);
    if (accommodation && (accommodation.cuntry_and_city || accommodation.name))
      return (accommodation.cuntry_and_city || accommodation.name).trim();
    const place = ((day && day.daily_attractions) || []).find(a => a && a.title && a.title.trim());
    return place ? place.title.trim() : '';
  });
}

/**
 * The splits (each stop's first day, from 1, ascending) after the manager
 * gives the stop at index one day more (delta 1) or one day less (-1), for a
 * trip of dayCount days. A day more is taken from the next stop, which goes
 * when it had only that day; a day less goes to the next stop, or after the
 * last stop becomes a stop of its own. A change that cannot be made (a day
 * more for the last stop, a day less for a one-day stop) returns the splits
 * as they were.
 */
function resizeStopSplits(splits, dayCount, index, delta) {
  const result = splits.slice();
  const next = index + 1;
  const lastDay = next < result.length ? result[next] - 1 : dayCount;
  if (delta > 0) {
    if (next >= result.length) return result;
    const moved = result[next] + 1;
    if (moved > dayCount || moved === result[next + 1]) result.splice(next, 1);
    else result[next] = moved;
  } else {
    if (lastDay === result[index]) return result;
    if (next < result.length) result[next] -= 1;
    else result.push(dayCount);
  }
  return result;
}

/**
 * The splits without the stop at index, its days joining the stop before it.
 * The first stop has none before it, so it stays.
 */
function removeStopSplit(splits, index) {
  return index > 0 ? splits.filter((day, i) => i !== index) : splits.slice();
}

/**
 * map (a stop's first day -> its name or photo) moved onto newSplits, so
 * every stop keeps its own after its first day moved. removedIndex is the stop
 * that went (-1 for none); a stop that is new gets nothing.
 */
function carryStopKeys(map, oldSplits, newSplits, removedIndex = -1) {
  const kept = oldSplits.filter((day, i) => i !== removedIndex);
  const carried = {};
  kept.forEach((oldDay, i) => {
    const value = map[String(oldDay)];
    if (value && i < newSplits.length) carried[String(newSplits[i])] = value;
  });
  return carried;
}

module.exports = {
  carryStopKeys,
  resizeStopSplits,
  removeStopSplit,
  POSTER_SETTINGS_VERSION,
  POSTER_NAME_MAX_LENGTH,
  buildPosterStops,
  posterDayNames,
  normalizeSplits,
  posterPhotoOptions,
  PER_STAY_ABOVE,
  MAX_POSTER_STOPS,
};
